import { useMemo, useRef, useState } from 'react';

import {
  DEFAULT_GAMEWEEK_HORIZON,
  MAX_GAMEWEEK,
  MIN_GAMEWEEK
} from './constants';
import { summariseFixtureValues } from './values';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useTeamFixtures } from '@hooks/useTeamFixtures';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import type {
  TeamFixturesSortBy,
  TeamFixturesSortDirection,
  TeamWithFixtures
} from '@api-service/team-fixtures';

export const FILTER_OPTIONS = [
  { id: 'overall', label: 'Overall' },
  { id: 'attack', label: 'Attack' },
  { id: 'defence', label: 'Defence' },
  { id: 'goals', label: 'Goals' },
  { id: 'cleanSheets', label: 'Clean sheets' }
] as const;

export type FilterOption = (typeof FILTER_OPTIONS)[number]['id'];

const FILTER_TO_SORT_BY: Record<FilterOption, TeamFixturesSortBy> = {
  overall: 'difficulty.overall',
  attack: 'difficulty.attack',
  defence: 'difficulty.defence',
  goals: 'goalsFor',
  cleanSheets: 'cleanSheet'
};

// For goals scored and clean-sheet probability, a higher value means an easier
// outlook, so the chosen direction is flipped to keep
// "easiest first" = ascending across all filter options.
const INVERTED_SORT_METRICS: ReadonlySet<TeamFixturesSortBy> = new Set([
  'goalsFor',
  'cleanSheet'
]);

// Metric-neutral wording: "easiest fixtures" reads wrongly when the table is
// sorted by expected goals or clean-sheet probability.
export const SORT_OPTIONS = [
  { id: 'best', label: 'Best runs first', direction: 'asc' as const },
  { id: 'worst', label: 'Worst runs first', direction: 'desc' as const }
] as const;

// Quick-set windows offered beside the gameweek slider.
export const GAMEWEEK_WINDOW_PRESETS = [3, 5, 8] as const;

type DifficultyMetric = 'overall' | 'attack' | 'defence';

const DIFFICULTY_METRIC_BY_SORT: Partial<
  Record<TeamFixturesSortBy, DifficultyMetric>
> = {
  'difficulty.overall': 'overall',
  'difficulty.attack': 'attack',
  'difficulty.defence': 'defence'
};

// The backend rounds aggregates.difficulty.* to whole numbers before ordering,
// which leaves most teams tied on the same integer with an arbitrary relative
// order that can contradict what the coloured cells show. Re-rank client-side
// by the exact mean of the visible per-fixture difficulties so the order
// always agrees with the cells.
const sortTeamsByExactDifficulty = (
  teams: TeamWithFixtures[],
  metric: DifficultyMetric,
  direction: TeamFixturesSortDirection,
  gameweekStart: number,
  gameweekEnd: number,
  hiddenGameweeks: number[]
): TeamWithFixtures[] => {
  // Shares its calculation with the summary column so the displayed number
  // always agrees with the order.
  const meanDifficulty = (team: TeamWithFixtures): number | null =>
    summariseFixtureValues(team.fixtures, metric, {
      gameweekStart,
      gameweekEnd,
      hiddenGameweeks
    });

  return [...teams].sort((a, b) => {
    const meanA = meanDifficulty(a);
    const meanB = meanDifficulty(b);
    // Teams with no rateable fixtures keep their backend order, at the end.
    if (meanA === null && meanB === null) return 0;
    if (meanA === null) return 1;
    if (meanB === null) return -1;
    return direction === 'asc' ? meanA - meanB : meanB - meanA;
  });
};

export type SortOption = (typeof SORT_OPTIONS)[number];

export type UseFixtureTickerFiltersOptions = {
  enabled?: boolean;
};

// Sentinel range used while league context is loading or the hook is disabled.
// gameweekEnd < gameweekStart makes useTeamFixtures skip the request until we
// know the real upcoming gameweek (avoiding a wasted GW1..GW6 query).
const INVALID_GAMEWEEK = 0;

// FFH-2373: the range slider reports every drag step, so the analytics
// capture waits for the drag to settle or one filter event per pixel goes out.
const GAMEWEEK_RANGE_CAPTURE_DEBOUNCE_MS = 800;

export const useFixtureTickerFilters = (
  options: UseFixtureTickerFiltersOptions = {}
) => {
  const { enabled = true } = options;
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('overall');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedSort, setSelectedSort] = useState<SortOption>(SORT_OPTIONS[0]);
  const [showTeamStrength, setShowTeamStrength] = useState(false);
  const [hiddenGameweeks, setHiddenGameweeks] = useState<number[]>([]);
  const [gameweekRange, setGameweekRange] = useState<{
    start: number;
    end: number;
  } | null>(null);

  const {
    context: leagueContext,
    isLoading: contextIsLoading,
    isError: contextIsError
  } = useLeagueContext({ enabled });

  // After the final gameweek the context still reports the finished season with
  // a null nextGameweek, but the schedule worth surfacing belongs to the new
  // season, which is announced before it kicks off. The backend resolves its
  // default season from the current gameweek, of which there is none in the
  // off-season, so it returns nothing unless we name the season. We therefore
  // aim the query at next season's opening gameweek ourselves and let the
  // response (hasFixtures) decide whether there is anything to show yet.
  const contextSeason = leagueContext?.season;
  const isPostSeason = leagueContext?.seasonStatus === 'POST_SEASON';
  const targetSeason =
    contextSeason === undefined
      ? undefined
      : isPostSeason
      ? contextSeason + 1
      : contextSeason;
  const upcomingGameweek =
    leagueContext?.nextGameweek?.number ??
    (isPostSeason ? MIN_GAMEWEEK : undefined);

  const isContextReady =
    upcomingGameweek !== undefined && targetSeason !== undefined;
  const defaultEnd = isContextReady
    ? Math.min(upcomingGameweek + DEFAULT_GAMEWEEK_HORIZON - 1, MAX_GAMEWEEK)
    : INVALID_GAMEWEEK;
  const gameweekStart =
    gameweekRange?.start ??
    (isContextReady ? upcomingGameweek : INVALID_GAMEWEEK);
  const gameweekEnd = gameweekRange?.end ?? defaultEnd;

  const sortBy = FILTER_TO_SORT_BY[selectedFilter];
  const isInverted = INVERTED_SORT_METRICS.has(sortBy);
  const sortDirection: TeamFixturesSortDirection = isInverted
    ? selectedSort.direction === 'asc'
      ? 'desc'
      : 'asc'
    : selectedSort.direction;

  // Hidden gameweeks are sent as excludeGameweeks so the backend re-ranks each
  // team over only the visible gameweeks - hiding a column should reorder the
  // table to reflect the gameweeks you actually care about. useTeamFixtures
  // keeps the previous data while refetching, so the table updates in place
  // rather than blanking. The hidden column itself stays put as a muted,
  // restorable placeholder (see FixtureAnalyserTable).
  const {
    teams: rankedTeams,
    isLoading: fixturesIsLoading,
    isError: fixturesIsError
  } = useTeamFixtures({
    gameweekStart,
    gameweekEnd,
    season: targetSeason,
    excludeGameweeks: hiddenGameweeks,
    sortBy,
    sortDirection
  });

  const difficultyMetric = DIFFICULTY_METRIC_BY_SORT[sortBy];
  const teams = useMemo(
    () =>
      difficultyMetric === undefined
        ? rankedTeams
        : sortTeamsByExactDifficulty(
            rankedTeams,
            difficultyMetric,
            sortDirection,
            gameweekStart,
            gameweekEnd,
            hiddenGameweeks
          ),
    [
      rankedTeams,
      difficultyMetric,
      sortDirection,
      gameweekStart,
      gameweekEnd,
      hiddenGameweeks
    ]
  );

  const gameweekNumbers = isContextReady
    ? Array.from(
        { length: gameweekEnd - gameweekStart + 1 },
        (_, index) => gameweekStart + index
      )
    : [];

  // FFH-2373: every filter interaction is an intent signal, captured here in
  // the hook so any consumer of these filters is measured. Sort direction has
  // no filter_type value in the tracking plan and stays unmeasured.
  const gameweekRangeCaptureTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const handleSelectedFilterChange = (filter: FilterOption) => {
    captureAnalyticsEvent(AnalyticsEvents.fixtureTickerFiltered, {
      filter_type: 'type'
    });
    setSelectedFilter(filter);
  };

  const handleSelectedTeamsChange = (teams: string[]) => {
    captureAnalyticsEvent(AnalyticsEvents.fixtureTickerFiltered, {
      filter_type: 'team'
    });
    setSelectedTeams(teams);
  };

  const handleShowTeamStrengthChange = (isVisible: boolean) => {
    captureAnalyticsEvent(AnalyticsEvents.fixtureTickerFiltered, {
      filter_type: 'team_strength'
    });
    setShowTeamStrength(isVisible);
  };

  const handleGameweekRangeChange = (start: number, end: number) => {
    setGameweekRange({ start, end });
    setHiddenGameweeks([]);
    if (gameweekRangeCaptureTimeoutRef.current) {
      clearTimeout(gameweekRangeCaptureTimeoutRef.current);
    }
    gameweekRangeCaptureTimeoutRef.current = setTimeout(() => {
      captureAnalyticsEvent(AnalyticsEvents.fixtureTickerFiltered, {
        filter_type: 'gameweek_range'
      });
    }, GAMEWEEK_RANGE_CAPTURE_DEBOUNCE_MS);
  };

  // Quick-set: keep the start and size the window to n gameweeks, clipped to
  // the end of the season.
  const handleGameweekWindowChange = (windowSize: number) => {
    handleGameweekRangeChange(
      gameweekStart,
      Math.min(gameweekStart + windowSize - 1, MAX_GAMEWEEK)
    );
  };

  const handleToggleHiddenGameweek = (gameweek: number) => {
    captureAnalyticsEvent(AnalyticsEvents.fixtureTickerFiltered, {
      filter_type: 'hidden_gameweeks'
    });
    setHiddenGameweeks((current) =>
      current.includes(gameweek)
        ? current.filter((value) => value !== gameweek)
        : [...current, gameweek]
    );
  };

  // Drive ticker visibility off the fixtures response rather than the calendar:
  // every team is always present in the payload, but each can have an empty
  // fixtures array (that is how blank gameweeks render). So "there is something
  // to show" means at least one team has at least one fixture in range. This is
  // false in the off-season until the new schedule is published, at which point
  // it flips to true on its own without any gameweek-number or flag change.
  const hasFixtures = teams.some((team) => team.fixtures.length > 0);

  return {
    teams,
    hasFixtures,
    isLoading: enabled && (contextIsLoading || fixturesIsLoading),
    isError: contextIsError || fixturesIsError,
    selectedFilter,
    setSelectedFilter: handleSelectedFilterChange,
    selectedTeams,
    setSelectedTeams: handleSelectedTeamsChange,
    selectedSort,
    setSelectedSort,
    showTeamStrength,
    setShowTeamStrength: handleShowTeamStrengthChange,
    hiddenGameweeks,
    setHiddenGameweeks,
    handleToggleHiddenGameweek,
    currentGameweek: upcomingGameweek ?? INVALID_GAMEWEEK,
    gameweekStart,
    gameweekEnd,
    gameweekNumbers,
    handleGameweekRangeChange,
    handleGameweekWindowChange,
    lastGameweek: MAX_GAMEWEEK
  };
};
